import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  listBondSubmissions,
  type BondListDecision,
  type BondSubmissionListItem,
  type BondSubmissionStatus,
} from "@/lib/brokerBonds";
import { BondOrdersTable } from "@/components/BondOrdersTable";
import { SelectField } from "@/components/SelectField";
import { messageFor } from "@/lib/errors";

// BRK-S174 — the brokerage's bond REQUESTS, as a panel rather than as a page.
//
// It was the whole of `BondSubmissionsPage` (BRK-S088). The Construction Bonds
// landing is now the second surface that shows the same list — a broker's front
// door should answer "where are my requests" without a click — so the list, its
// filters and its handled states are ONE component and the two surfaces are
// thin wrappers around it. Copying it onto the landing would have been copying
// the fetch, the filters, the empty state and the error state, which is four
// places to fix the next defect.
//
// ── THE FILTERS LIVE IN THE URL ──────────────────────────────────────────────
//
// `?status=` and `?decision=` are read from and written to the query string, so
// a filtered list is a page a broker can bookmark, reload and send to somebody
// — the same property BRK-S140 gave `?accountId=`, reached through controls
// rather than only through a link. Reloading a state only a click can produce
// is how a broker loses their place.
//
// They are applied SERVER-side. Filtering the fetched page in the client would
// hide rows the server never sent and still call itself a filter: a page of 25
// filtered down to 3 is not "your 3 approved requests".

const PAGE_SIZE = 25;

/** `?status=` values, as PMS's own vocabulary spells them.
 *
 *  `cancelled` (BRK-S181, PMS-S479) is the one way to see a discarded order:
 *  PMS EXCLUDES them from the unfiltered list, so "All statuses" means every
 *  live order and a cancelled one is reached by asking for it. The client does
 *  not filter anything itself — that default is the server's. */
const STATUS_OPTIONS: { value: BondSubmissionStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Filed with Signal" },
  { value: "cancelled", label: "Cancelled" },
];

/** `?decision=` values. Three words and nothing behind them (PMS-S461). */
const DECISION_OPTIONS: { value: BondListDecision; label: string }[] = [
  { value: "approved", label: "Approved" },
  { value: "referred", label: "Referred" },
  { value: "pending", label: "Pending" },
];

/** A query-string value, kept only when it is one this client asked for — a
 *  hand-edited URL must never put a value PMS would 400 on into a request. */
function oneOf<T extends string>(
  raw: string | null,
  allowed: readonly { value: T }[],
): T | undefined {
  return allowed.find((o) => o.value === raw)?.value;
}

export function BondRequestsPanel({
  accountId,
  emptyAction = true,
}: {
  /** Narrow to one insured (BRK-S140). Server-side and additive to the
   *  own-scoping — PMS still forces the brokerage from the session. */
  accountId?: string;
  /** Whether the empty state offers "start your first". The landing already
   *  leads with that button, so it says so once. */
  emptyAction?: boolean;
}) {
  const { authedRequest } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = oneOf(params.get("status"), STATUS_OPTIONS);
  const decision = oneOf(params.get("decision"), DECISION_OPTIONS);
  const [rows, setRows] = useState<BondSubmissionListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setError(null);
    setRows(null);
    (async () => {
      try {
        const page = await listBondSubmissions(authedRequest, {
          accountId,
          status,
          decision,
          limit: PAGE_SIZE,
        });
        if (!cancelled) setRows(page.items);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId, status, decision]);

  useEffect(() => load(), [load]);

  /** Set or clear one filter, leaving every other query parameter alone —
   *  `?accountId=` in particular, which is somebody else's narrowing. */
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const filtered = status !== undefined || decision !== undefined;

  return (
    <div className="flex flex-col gap-4">
      {/* Stacked and full-width at 375, side by side from `sm:` up. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
        <div className="w-full sm:w-56">
          <SelectField
            label="Status"
            placeholder="All statuses"
            options={STATUS_OPTIONS}
            value={status ?? ""}
            onChange={(e) => setFilter("status", e.target.value)}
          />
        </div>
        <div className="w-full sm:w-56">
          <SelectField
            label="Decision"
            placeholder="All decisions"
            options={DECISION_OPTIONS}
            value={decision ?? ""}
            onChange={(e) => setFilter("decision", e.target.value)}
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {rows === null && !error && (
        <p role="status" className="text-sm text-brk-dim">
          Loading bond submissions…
        </p>
      )}

      {rows !== null && rows.length === 0 && (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10">
          {/* "None match" and "none at all" are different facts, and only one
              of them means the broker has nothing on with Signal. Offering
              "start your first" to somebody who has twelve requests and a
              filter on would be nonsense. */}
          <p className="text-sm text-brk-text">
            {filtered
              ? "No bond requests match these filters."
              : "No bond submissions yet."}
          </p>
          {filtered ? (
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("status");
                next.delete("decision");
                setParams(next, { replace: true });
              }}
              className="text-sm font-medium text-brk-accent hover:underline"
            >
              Clear filters
            </button>
          ) : (
            emptyAction && (
              <Link
                to="/surety/submissions/new"
                className="text-sm font-medium text-brk-accent hover:underline"
              >
                Start your first bond submission →
              </Link>
            )
          )}
        </div>
      )}

      {rows !== null && rows.length > 0 && <BondOrdersTable rows={rows} />}
    </div>
  );
}
